import { formatMetric, roleHas, type CreativeAssetDto } from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  Folder,
  ImageIcon,
  RefreshCw,
  Send,
  Tag,
  Trash2,
  UploadCloud,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AccountPicker, readLastAccount } from "@/components/campaigns/account-picker";
import { ClientFilter, ClientTag } from "@/components/client-filter";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { AssetThumb } from "@/components/creation/asset-picker";
import { UploadDialog } from "@/components/creation/upload-dialog";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { errorMessage } from "@/lib/api";
import { creationApi } from "@/lib/creation-api";
import { queries } from "@/lib/queries";
import { cn, formatRelative } from "@/lib/utils";

export const Route = createFileRoute("/app/$workspaceId/criativos")({ component: CriativosPage });

const TODAS = "__todas__";
const UPLOAD_LABEL: Record<
  string,
  { label: string; variant: "success" | "warning" | "destructive" | "secondary" }
> = {
  pronto: { label: "Pronto na conta", variant: "success" },
  processando: { label: "Processando na Meta", variant: "warning" },
  enviando: { label: "Enviando", variant: "warning" },
  erro: { label: "Erro", variant: "destructive" },
};

function CriativosPage() {
  const { workspaceId } = Route.useParams();
  const queryClient = useQueryClient();
  const workspace = useQuery(queries.workspace(workspaceId));
  const canWrite = workspace.data ? roleHas(workspace.data.role, "campaigns:write") : false;
  const clients = useQuery(queries.clients(workspaceId));
  const accounts = useQuery(queries.campaignAccounts(workspaceId));
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const [folder, setFolder] = useState(TODAS);
  const [tag, setTag] = useState(TODAS);
  const [kind, setKind] = useState(TODAS);
  const [q, setQ] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<CreativeAssetDto | null>(null);
  const [account, setAccount] = useState(() => readLastAccount(workspaceId) ?? "");

  const params: Record<string, string> = {};
  if (clientId) params.clientId = clientId;
  if (folder !== TODAS) params.folder = folder;
  if (tag !== TODAS) params.tag = tag;
  if (kind !== TODAS) params.kind = kind;
  if (q.trim()) params.q = q.trim();
  const list = useQuery(queries.creatives(workspaceId, params));
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "creatives"] });

  const push = useMutation({
    mutationFn: (input: { assetId: string; account: string }) =>
      creationApi.pushAsset(workspaceId, input.assetId, input.account),
    onSuccess: async (u) => {
      await invalidate();
      toast.success(
        u.status === "pronto" ? "Enviado para a conta." : "Vídeo enviado; a Meta está processando.",
      );
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const refresh = useMutation({
    mutationFn: (input: { assetId: string; account: string }) =>
      creationApi.refreshUpload(workspaceId, input.assetId, input.account),
    onSuccess: async () => {
      await invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: (assetId: string) => creationApi.deleteAsset(workspaceId, assetId),
    onSuccess: async () => {
      await invalidate();
      setToDelete(null);
      setSelected(null);
      toast.success("Criativo excluído da biblioteca.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const currentAccount = account || (accounts.data?.[0]?.metaId ?? "");
  // Sempre a versão mais recente do criativo (envios e status mudam enquanto o painel está aberto).
  const selected = selectedId ? (list.data?.items.find((i) => i.id === selectedId) ?? null) : null;
  const setSelected = (a: CreativeAssetDto | null) => setSelectedId(a?.id ?? null);

  return (
    <>
      <PageHeader
        title="Criativos"
        description="Biblioteca de imagens e vídeos do workspace. Envie uma vez, use em qualquer conta."
        actions={
          canWrite ? (
            <Button onClick={() => setUploadOpen(true)}>
              <UploadCloud />
              Enviar criativo
            </Button>
          ) : null
        }
      />
      {list.isPending ? (
        <LoadingState rows={4} />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <ClientFilter workspaceId={workspaceId} value={clientId} onChange={setClientId} />
            <Input
              placeholder="Buscar por nome…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="sm:max-w-xs"
              aria-label="Buscar criativo"
            />
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="sm:w-36" aria-label="Tipo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODAS}>Imagens e vídeos</SelectItem>
                <SelectItem value="imagem">Só imagens</SelectItem>
                <SelectItem value="video">Só vídeos</SelectItem>
              </SelectContent>
            </Select>
            <Select value={folder} onValueChange={setFolder}>
              <SelectTrigger className="sm:w-44" aria-label="Pasta">
                <Folder className="size-4 text-muted-foreground" aria-hidden />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODAS}>Todas as pastas</SelectItem>
                {list.data.folders.map((f) => (
                  <SelectItem key={f.name} value={f.name}>
                    {f.name} ({f.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={tag} onValueChange={setTag}>
              <SelectTrigger className="sm:w-40" aria-label="Tag">
                <Tag className="size-4 text-muted-foreground" aria-hidden />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODAS}>Todas as tags</SelectItem>
                {list.data.tags.map((t) => (
                  <SelectItem key={t.name} value={t.name}>
                    {t.name} ({t.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {list.data.items.length === 0 ? (
            <EmptyState
              icon={<ImageIcon className="size-8" aria-hidden />}
              title="Nenhum criativo"
              description={
                Object.keys(params).length > 0
                  ? "Nada com esses filtros."
                  : "Envie imagens e vídeos para usar nos anúncios."
              }
              action={
                canWrite ? (
                  <Button onClick={() => setUploadOpen(true)}>Enviar criativo</Button>
                ) : undefined
              }
            />
          ) : (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {list.data.items.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    className={cn(
                      "w-full overflow-hidden rounded-lg border bg-card text-left hover:ring-2 hover:ring-ring",
                      a.status === "erro" && "border-destructive",
                    )}
                    onClick={() => setSelected(a)}
                  >
                    <AssetThumb asset={a} className="aspect-square w-full" />
                    <div className="space-y-1 p-2">
                      <p className="truncate text-sm font-medium">{a.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {a.kind === "video" ? "Vídeo" : "Imagem"} ·{" "}
                        {a.width && a.height ? `${a.width}×${a.height}` : "—"}
                        {a.folder ? ` · ${a.folder}` : ""}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {a.status === "erro" ? <Badge variant="destructive">Erro</Badge> : null}
                        {a.status === "enviando" ? <Badge variant="warning">Enviando</Badge> : null}
                        {a.uploads.filter((u) => u.status === "pronto").length > 0 ? (
                          <Badge variant="success">
                            {a.uploads.filter((u) => u.status === "pronto").length} conta(s)
                          </Badge>
                        ) : null}
                        {a.warnings.length > 0 ? (
                          <Badge variant="warning">{a.warnings.length} aviso(s)</Badge>
                        ) : null}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Card>
            <CardContent className="text-sm text-muted-foreground">
              <strong className="text-foreground">Desempenho por criativo</strong> — abra um
              criativo para ver as métricas somadas de todos os anúncios que usam a mesma imagem ou
              vídeo (cruzamento pelo hash/id enviado para cada conta).
            </CardContent>
          </Card>
        </div>
      )}

      <UploadDialog workspaceId={workspaceId} open={uploadOpen} onOpenChange={setUploadOpen} />

      {selected ? (
        <Dialog open onOpenChange={(o) => (o ? undefined : setSelected(null))}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selected.name}</DialogTitle>
              <DialogDescription>
                {selected.kind === "video" ? "Vídeo" : "Imagem"} · {selected.mimeType} ·{" "}
                {(selected.sizeBytes / 1024 / 1024).toFixed(2)} MB
                {selected.width && selected.height ? ` · ${selected.width}×${selected.height}` : ""}
                {selected.durationMs ? ` · ${Math.round(selected.durationMs / 1000)} s` : ""} ·
                enviado {formatRelative(selected.createdAt)}
                {selected.uploadedByName ? ` por ${selected.uploadedByName}` : ""}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 sm:grid-cols-[16rem_1fr]">
              {selected.kind === "video" ? (
                <video src={selected.fileUrl} controls className="w-full rounded-md bg-black" />
              ) : (
                <img src={selected.fileUrl} alt="" className="w-full rounded-md" />
              )}
              <div className="space-y-3 text-sm">
                <p>
                  <span className="text-muted-foreground">Cliente: </span>
                  <ClientTag clientId={selected.clientId} clients={clients.data} />
                </p>
                <p>
                  <span className="text-muted-foreground">Pasta: </span>
                  {selected.folder || "—"} · <span className="text-muted-foreground">Tags: </span>
                  {selected.tags.length > 0 ? selected.tags.join(", ") : "—"}
                </p>
                {selected.warnings.length > 0 ? (
                  <ul className="space-y-1">
                    {selected.warnings.map((w, i) => (
                      <li
                        key={i}
                        className={
                          w.severity === "erro"
                            ? "text-destructive"
                            : "text-amber-700 dark:text-amber-400"
                        }
                      >
                        <AlertTriangle className="mr-1 inline size-3.5" aria-hidden />
                        {w.message}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-emerald-700 dark:text-emerald-400">
                    Dentro das recomendações da Meta para todos os posicionamentos.
                  </p>
                )}
                <AssetPerformance workspaceId={workspaceId} assetId={selected.id} />
                <div className="space-y-2 rounded-md border p-3">
                  <p className="font-medium">Contas de anúncio</p>
                  {selected.uploads.length === 0 ? (
                    <p className="text-muted-foreground">
                      Ainda não foi enviado para nenhuma conta.
                    </p>
                  ) : null}
                  <ul className="space-y-1">
                    {selected.uploads.map((u) => (
                      <li key={u.adAccountMetaId} className="flex flex-wrap items-center gap-2">
                        <span>
                          {accounts.data?.find((a) => a.metaId === u.adAccountMetaId)?.name ??
                            u.adAccountMetaId}
                        </span>
                        <Badge variant={UPLOAD_LABEL[u.status]?.variant ?? "secondary"}>
                          {UPLOAD_LABEL[u.status]?.label ?? u.status}
                        </Badge>
                        {u.imageHash ? (
                          <code className="text-xs text-muted-foreground">
                            hash {u.imageHash.slice(0, 10)}…
                          </code>
                        ) : null}
                        {u.videoId ? (
                          <code className="text-xs text-muted-foreground">vídeo {u.videoId}</code>
                        ) : null}
                        {u.status === "processando" ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={refresh.isPending}
                            onClick={() =>
                              refresh.mutate({ assetId: selected.id, account: u.adAccountMetaId })
                            }
                          >
                            <RefreshCw />
                            Conferir
                          </Button>
                        ) : null}
                        {u.error ? (
                          <span className="text-xs text-destructive">{u.error}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  {canWrite && selected.status === "pronto" && (accounts.data?.length ?? 0) > 0 ? (
                    <div className="flex flex-col gap-2 pt-2 sm:flex-row">
                      <AccountPicker
                        accounts={accounts.data ?? []}
                        value={currentAccount}
                        onChange={setAccount}
                      />
                      <Button
                        variant="outline"
                        loading={push.isPending}
                        disabled={!currentAccount}
                        onClick={() =>
                          push.mutate({ assetId: selected.id, account: currentAccount })
                        }
                      >
                        <Send />
                        Enviar para a conta
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            {canWrite ? (
              <DialogFooter className="sm:justify-between">
                <Button
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => setToDelete(selected)}
                >
                  <Trash2 />
                  Excluir da biblioteca
                </Button>
                <EditAssetForm
                  workspaceId={workspaceId}
                  asset={selected}
                  onSaved={() => undefined}
                />
              </DialogFooter>
            ) : null}
          </DialogContent>
        </Dialog>
      ) : null}

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(o) => (o ? undefined : setToDelete(null))}
        title={`Excluir "${toDelete?.name ?? ""}" da biblioteca?`}
        description="O arquivo some do armazenamento. Anúncios já publicados na Meta continuam, porque a imagem/vídeo já está na conta de anúncio."
        confirmLabel="Excluir"
        destructive
        loading={remove.isPending}
        onConfirm={() => (toDelete ? remove.mutate(toDelete.id) : undefined)}
      />
    </>
  );
}

/** Métricas somadas dos anúncios que usam o criativo (Fase 6). */
function AssetPerformance({ workspaceId, assetId }: { workspaceId: string; assetId: string }) {
  const [preset, setPreset] = useState("30d");
  const perf = useQuery(queries.assetPerformance(workspaceId, assetId, preset));
  const accounts = useQuery(queries.campaignAccounts(workspaceId));
  const currency =
    accounts.data?.find((a) => a.metaId === perf.data?.accounts[0])?.currency ?? "BRL";
  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium">Desempenho</p>
        <Select value={preset} onValueChange={setPreset}>
          <SelectTrigger className="h-7 w-36 text-xs" aria-label="Período do desempenho">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Últimos 7 dias</SelectItem>
            <SelectItem value="30d">Últimos 30 dias</SelectItem>
            <SelectItem value="mes_passado">Mês passado</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {perf.isPending ? (
        <LoadingState rows={1} />
      ) : perf.isError ? (
        <p className="text-xs text-destructive">{errorMessage(perf.error)}</p>
      ) : perf.data.adCount === 0 ? (
        <p className="text-xs text-muted-foreground">
          Nenhum anúncio com métricas usa este criativo (ele precisa ter sido enviado para a conta e
          usado num anúncio já sincronizado).
        </p>
      ) : (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
          <div>
            <dt className="text-muted-foreground">Anúncios</dt>
            <dd className="font-medium">
              {perf.data.adCount} em {perf.data.accounts.length} conta(s)
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Gasto</dt>
            <dd className="font-medium">
              {formatMetric({ format: "money" }, perf.data.metrics.spendCents, currency)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Impressões</dt>
            <dd className="font-medium">
              {formatMetric({ format: "int" }, perf.data.metrics.impressions, currency)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">CTR</dt>
            <dd className="font-medium">
              {formatMetric({ format: "pct" }, perf.data.metrics.ctr, currency)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">CPC</dt>
            <dd className="font-medium">
              {formatMetric({ format: "money" }, perf.data.metrics.cpcCents, currency)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Resultados</dt>
            <dd className="font-medium">
              {formatMetric({ format: "int" }, perf.data.metrics.results, currency)}
              {perf.data.metrics.costPerResultCents !== null
                ? ` (${formatMetric({ format: "money" }, perf.data.metrics.costPerResultCents, currency)} cada)`
                : ""}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}

function EditAssetForm({
  workspaceId,
  asset,
  onSaved,
}: {
  workspaceId: string;
  asset: CreativeAssetDto;
  onSaved: (a: CreativeAssetDto) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(asset.name);
  const [folder, setFolder] = useState(asset.folder);
  const [tags, setTags] = useState(asset.tags.join(", "));
  const save = useMutation({
    mutationFn: () =>
      creationApi.updateAsset(workspaceId, asset.id, {
        name,
        folder,
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      }),
    onSuccess: async (a) => {
      await queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "creatives"] });
      onSaved(a);
      toast.success("Criativo atualizado.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <form
      className="flex flex-col gap-2 sm:flex-row sm:items-end"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="ed-nome" className="text-xs">
          Nome
        </Label>
        <Input
          id="ed-nome"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-8"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="ed-pasta" className="text-xs">
          Pasta
        </Label>
        <Input
          id="ed-pasta"
          value={folder}
          onChange={(e) => setFolder(e.target.value)}
          className="h-8"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="ed-tags" className="text-xs">
          Tags
        </Label>
        <Input
          id="ed-tags"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          className="h-8"
        />
      </div>
      <Button type="submit" size="sm" loading={save.isPending}>
        Salvar
      </Button>
    </form>
  );
}
